
'use client';

import * as React from 'react';
import { useHydration } from '@/hooks/use-hydration';

export type Currency = 'NOK' | 'USD' | 'GBP' | 'EUR';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  getCurrencySymbol: (currencyCode?: string) => string;
}

const CurrencyContext = React.createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_STORAGE_KEY = 'emperorEscapesCurrency';

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  // Always initialize with the default state on both server and client.
  const [currency, setCurrencyState] = React.useState<Currency>('NOK');
  const isHydrated = useHydration();

  // After the initial render (hydration), check localStorage on the client.
  React.useEffect(() => {
    if (!isHydrated) return;
    
    try {
        const savedCurrency = window.localStorage.getItem(CURRENCY_STORAGE_KEY) as Currency;
        if (savedCurrency && savedCurrency !== currency) {
          setCurrencyState(savedCurrency);
        }
    } catch (e) {
        console.error('Could not access local storage to get currency.');
    }
    // Only run once after hydration - remove currency from deps to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]);


  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    if (isHydrated && typeof window !== 'undefined') {
      try {
        localStorage.setItem(CURRENCY_STORAGE_KEY, newCurrency);
      } catch (e) {
        console.error('Could not access local storage to set currency.');
      }
    }
  };

  const getCurrencySymbol = (currencyCode: string | undefined): string => {
    const code = currencyCode || currency;
    switch (code) {
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'EUR': return '€';
      case 'NOK': return 'kr';
      default: return 'kr';
    }
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, getCurrencySymbol }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = React.useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
